import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useSession, useSignOutMutation } from "@/features/Auth/service/query";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: sessionData, isLoading } = useSession();
  const signOutMutation = useSignOutMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const hasSession = !!sessionData?.session;
      setIsAuthenticated(hasSession);
      setHasCheckedAuth(true);

      localStorage.setItem(
        "orgId",
        sessionData?.user?.organizations[0].id ?? ""
      );

      // Check if current path requires auth and user is not authenticated
      const isProtectedRoute = !(
        location.pathname.startsWith("/auth") ||
        location.pathname.startsWith("/bp/")
      );
      const isApiRoute = location.pathname.startsWith("/api/");

      if (!hasSession && isProtectedRoute && !isApiRoute) {
        // Store the attempted URL for redirect after login
        const callbackUrl = location.pathname + location.search;
        navigate(`/auth/login?callbackUrl=${encodeURIComponent(callbackUrl)}`, {
          replace: true,
        });
      }
    }
  }, [sessionData, isLoading, navigate, location]);

  const signOut = () => {
    signOutMutation.mutate();
  };

  const value = {
    isAuthenticated,
    isLoading: isLoading || !hasCheckedAuth,
    user: sessionData?.user || null,
    signOut,
  };

  // Show loading state while checking authentication
  if (!hasCheckedAuth || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
